import {DrawFrmThing} from "../../drawfrm/DrawFrmThing";
import {ElevationApp} from "../../ElevationApp";
import {LinearDimension} from "../frameGrid/LinearDimension";
import {inches, linePrepYellow, linePrepYellowUser, prepBandAlpha} from "../../const";
import {drawHatchRect} from "../../shared-2d/cutHatch";

// perpendicular gap between the diagonal hatch lines shading the closer band.
const BAND_HATCH_SPACING = 1 / 2 * inches;
import {AnchorKind, closerPrepRect, PREP_HOLE_DIAMETER} from "../../model/hardware/MdHardwareRules";
import {CrosshairRect} from "./HardwareCrosshairThing";

// the prep layer of one door leaf, in yellow: the 161 and DB holes with their offsets
// (height above the finished floor, backset from the strike edge) and the closer band
// across the top of the leaf. A prep draws while a placement sits on it (see
// MdHardware.prepsUsed). The offsets are the only dimensions door hardware has: editing
// one moves the anchor, and every placement on it (see MdHardware.moveAnchor).
export class DoorPrepsThing extends DrawFrmThing {
    private elev: ElevationApp;
    private side: "active" | "inactive";
    private rect: () => CrosshairRect | null;
    private mirror: () => boolean;
    private dims: Record<AnchorKind, {vertical: LinearDimension; horizontal: LinearDimension}>;

    constructor(props: {
        elev: ElevationApp;
        side: "active" | "inactive";
        // the leaf rect in this Thing's local coords.
        rect: () => CrosshairRect | null;
        // when true, the leaf is shown mirrored left-to-right (the Locations view's back face).
        mirror?: () => boolean;
    }) {
        super();
        this.elev = props.elev;
        this.side = props.side;
        this.rect = props.rect;
        this.mirror = props.mirror ?? (() => false);

        const make = () => ({
            vertical: new LinearDimension(this.lines, this.elev.draw),
            horizontal: new LinearDimension(this.lines, this.elev.draw),
        });
        this.dims = {"161": make(), db: make()};

        // redraw as a prep moves, or as placements link / unlink (which preps are in use).
        const anchors = this.elev.hardware.prepAnchors[this.side];
        for(const kind of ["161", "db"] as const) {
            this.trackSubscription(anchors[kind].aboveFloor.subscribe(() => this.safeDraw()));
            this.trackSubscription(anchors[kind].fromStrike.subscribe(() => this.safeDraw()));
        }
        this.trackSubscription(this.elev.hardware.listChanged.subscribe(() => this.safeDraw()));

        this.draw();
    }

    private safeDraw() {
        if(!this.destroyed) this.draw();
    }

    draw() {
        const g = this.lines;
        g.clear();

        const r = this.rect();
        const hardware = this.elev.hardware;
        const used = this.elev.viewSettings.frameOnly() ? [] : hardware.prepsUsed(this.side);
        if(!r) {
            this.hideAll();
            return;
        }

        const W = r.x1 - r.x0;
        const H = r.y1 - r.y0;
        const leaf = hardware.leafFor(this.side);
        const mirrored = this.mirror();
        // the strike edge is opposite the hinges; flipped when the leaf is shown mirrored.
        const strikeOnLeft = (leaf.hingeSide === "right") !== mirrored;
        const strikeX = strikeOnLeft ? r.x0 : r.x1;
        const floorY = r.y0 + H + leaf.undercut;

        for(const kind of ["161", "db"] as const) {
            const dims = this.dims[kind];
            if(!used.includes(kind)) {
                dims.vertical.hide();
                dims.horizontal.hide();
                continue;
            }

            const pos = hardware.anchorPosition(this.side, kind);
            const values = hardware.anchor(this.side, kind);
            const vars = hardware.prepAnchors[this.side][kind];
            const cx = r.x0 + (mirrored ? W - pos.x : pos.x);
            const cy = r.y0 + pos.y;

            g.lineStyle(2, linePrepYellow, 1);
            g.drawCircle(cx, cy, PREP_HOLE_DIAMETER / 2);

            dims.vertical.draw({
                axis: "vertical", from: cy, to: floorY, cross: cx,
                value: values.aboveFloor, modified: vars.aboveFloor.isModified(),
                color: linePrepYellow, modifiedColor: linePrepYellowUser,
                onSet: v => vars.aboveFloor.userSet(v, {debug: `hardware.${kind}.${this.side}.aboveFloor`}),
                onClear: () => vars.aboveFloor.userClearModified(),
            });
            dims.horizontal.draw({
                axis: "horizontal", from: strikeX, to: cx, cross: cy,
                value: values.fromStrike, modified: vars.fromStrike.isModified(),
                color: linePrepYellow, modifiedColor: linePrepYellowUser,
                onSet: v => vars.fromStrike.userSet(v, {debug: `hardware.${kind}.${this.side}.fromStrike`}),
                onClear: () => vars.fromStrike.userClearModified(),
            });
        }

        if(used.includes("closer")) {
            const band = closerPrepRect(leaf);
            const x = r.x0 + band.x0, y = r.y0 + band.y0, w = band.x1 - band.x0, h = band.y1 - band.y0;
            g.lineStyle(0);
            g.beginFill(linePrepYellow, prepBandAlpha);
            g.drawRect(x, y, w, h);
            g.endFill();
            g.lineStyle(1, linePrepYellow, 0.8);
            drawHatchRect(g, x, y, w, h, BAND_HATCH_SPACING);
            g.lineStyle(2, linePrepYellow, 1);
            g.drawRect(x, y, w, h);
        }
    }

    private hideAll() {
        for(const kind of ["161", "db"] as const) {
            this.dims[kind].vertical.hide();
            this.dims[kind].horizontal.hide();
        }
    }

    destroy() {
        if(this.destroyed) return;
        for(const kind of ["161", "db"] as const) {
            this.dims[kind].vertical.destroy();
            this.dims[kind].horizontal.destroy();
        }
        super.destroy();
    }
}
